import { useMemo } from 'react';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import type { LayoutItem, Layout } from 'react-grid-layout';
import type { BIQueryError, BiWidget, DateRange, FilterCondition } from '../../../types/bi';
import type { DrilldownEvent, DrilldownFilter } from '../utils/drilldown';
import LiveWidget from './LiveWidget';
import {
  useGridLayout,
  generateResponsiveLayouts,
  BREAKPOINTS,
  COLS,
  ROW_HEIGHT,
} from '../hooks/useGridLayout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import styles from './DashboardGrid.module.css';

interface DashboardGridProps {
  widgets: BiWidget[];
  dateRange?: DateRange | null;
  globalFilters?: FilterCondition[];
  drilldownFilters?: DrilldownFilter[];
  explainDebugEnabled?: boolean;
  onSavePositions: (
    positions: Array<{ id: number; x: number; y: number; w: number; h: number }>,
  ) => void;
  onConfigureWidget: (widget: BiWidget) => void;
  onRemoveWidget: (widgetId: number) => void;
  onRenameWidget?: (widgetId: number, newTitle: string) => void;
  onDrillDown?: (widgetId: number, event: DrilldownEvent) => void;
  onWidgetQueryErrorChange?: (widgetId: number, error: BIQueryError | null) => void;
  onCreateAlert?: (widget: BiWidget) => void;
  readOnly?: boolean;
}

export default function DashboardGrid({
  widgets,
  dateRange,
  globalFilters,
  drilldownFilters,
  explainDebugEnabled = false,
  onSavePositions,
  onConfigureWidget,
  onRemoveWidget,
  onRenameWidget,
  onDrillDown,
  onWidgetQueryErrorChange,
  onCreateAlert,
  readOnly = false,
}: DashboardGridProps) {
  const { containerRef, width } = useContainerWidth();

  const initialLayout: LayoutItem[] = widgets.map((w) => ({
    i: String(w.id),
    x: w.position.x,
    y: w.position.y,
    w: w.position.w,
    h: w.position.h,
    minW: 2,
    minH: 2,
  }));

  const { layout, isDirty, currentBreakpoint, handleLayoutChange, handleBreakpointChange } =
    useGridLayout({
      initialLayout,
      onSave: onSavePositions,
    });

  // Generate responsive layouts for all breakpoints from the current lg layout
  const responsiveLayouts = useMemo(() => generateResponsiveLayouts(layout), [layout]);

  // Disable drag & resize on small breakpoints (sm/xs) — touch-friendly view-only
  const isMobile = currentBreakpoint === 'sm' || currentBreakpoint === 'xs';
  const dragEnabled = !readOnly && !isMobile;

  return (
    <div className={styles.gridContainer} ref={containerRef}>
      {isDirty && <div className={styles.savingIndicator}>Saving layout…</div>}
      <Responsive
        className={styles.grid}
        width={width}
        layouts={responsiveLayouts as Record<string, Layout>}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        dragConfig={{
          enabled: dragEnabled,
          handle: '.widget-drag-handle',
          bounded: false,
          threshold: 3,
        }}
        resizeConfig={{ enabled: dragEnabled, handles: ['se'] }}
        onLayoutChange={(newLayout: Layout) => handleLayoutChange([...newLayout])}
        onBreakpointChange={handleBreakpointChange}
        margin={[12, 12] as [number, number]}
      >
        {widgets.map((widget) => (
          <div key={String(widget.id)}>
            <LiveWidget
              widget={widget}
              dateRange={dateRange}
              globalFilters={globalFilters}
              drilldownFilters={drilldownFilters}
              explainDebugEnabled={explainDebugEnabled}
              onConfigure={() => onConfigureWidget(widget)}
              onRemove={() => onRemoveWidget(widget.id)}
              onRename={
                onRenameWidget ? (newTitle) => onRenameWidget(widget.id, newTitle) : undefined
              }
              onDrillDown={onDrillDown ? (event) => onDrillDown(widget.id, event) : undefined}
              onQueryErrorChange={onWidgetQueryErrorChange}
              onCreateAlert={onCreateAlert ? () => onCreateAlert(widget) : undefined}
              readOnly={readOnly || isMobile}
            />
          </div>
        ))}
      </Responsive>
    </div>
  );
}
